import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  UpcomingEventsState,
  Holiday,
  LocalEvent,
  EventMaster,
  CombinedEvent,
  UnifiedEvent,
} from '../../components/upcomingEvents/types';

const initialState: UpcomingEventsState = {
  holidays: [],
  localEvents: [],
  eventMaster: [],
  combinedEvents: [],
  loading: false,
  error: null,
  countryCode: 'US', // Default country code
};

const upcomingEventsSlice = createSlice({
  name: 'upcomingEvents',
  initialState,
  reducers: {
    // Fetch upcoming events (triggers saga to fetch unified API)
    fetchUpcomingEventsRequest(state, action: PayloadAction<{ restaurantId: string; countryCode: string }>) {
      state.loading = true;
      state.error = null;
      state.countryCode = action.payload.countryCode;
    },
    
    fetchUpcomingEventsSuccess(
      state,
      action: PayloadAction<{
        unifiedEvents: UnifiedEvent[];
      }>
    ) {
      // Clear old data structures
      state.holidays = [];
      state.localEvents = [];
      state.eventMaster = [];
      state.loading = false;
      state.error = null;
      
      // Convert unified events to combined events format
      state.combinedEvents = convertUnifiedEventsToCombinedEvents(action.payload.unifiedEvents);
    },
    
    fetchUpcomingEventsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    
    setCountryCode(state, action: PayloadAction<string>) {
      state.countryCode = action.payload;
    },
  },
});

function convertUnifiedEventsToCombinedEvents(unifiedEvents: UnifiedEvent[]): CombinedEvent[] {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  
  const processedEvents: CombinedEvent[] = unifiedEvents
    .filter(event => {
      const eventDate = new Date(event.startDate);
      return eventDate.getFullYear() === currentYear;
    })
    .map(event => ({
      id: `unified-event-${event.id}`,
      name: event.eventName,
      date: event.startDate,
      country_code: event.countryCode,
      type: event.eventType || event.category,
      category: 'Event Master' as const, // Default category for unified events
      venue: event.venue,
      location: event.location,
      state: event.state,
      source: event.source,
      consumer_behavior: event.description,
      marketing_angle: event.subCategory,
    }));
  
  // Remove duplicates
  const uniqueEvents = processedEvents.filter((event, index, self) =>
    index === self.findIndex((e) => 
      e.name.toLowerCase() === event.name.toLowerCase() && e.date === event.date
    )
  );
  
  // Sort by date
  return uniqueEvents.sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
}

export const {
  fetchUpcomingEventsRequest,
  fetchUpcomingEventsSuccess,
  fetchUpcomingEventsFailure,
  setCountryCode,
} = upcomingEventsSlice.actions;

export default upcomingEventsSlice.reducer;
